import Layout from '@components/Layout';
import Table, { IColumnTable } from '@components/table-devextreme/Table';
import TableTitle from '@components/table-devextreme/TableTitle';
import { RootState } from 'app/store';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { EModePanelSelect, ICallBackEvents, SelectPanel } from '../../../components/SelectPanelReport/SelectPanel';
import { message } from 'antd';
import { ThunkDispatch } from '@reduxjs/toolkit';
import moment from 'moment';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';

import { getAllTypeOrders, getInitProductMixData } from '@slice/createOrder.slice';
import { getAllBranch, updatePageLoadingApi } from '@slice/select.slice';
import { MINPAGESIZE3 } from 'global/Global';
import UtilOrder, { convertTime } from '../../OrganizedSales/CreateOrders/utilOrder';
import { useOrderReportV5 } from 'apis/queries';
import Helpers from 'helpers/Helpers';
import OrderManagerApi from 'apis/api/OrderManager';

// ============================================================================
// Hằng số / helper thuần (nằm ngoài component, không tạo lại mỗi render)
// ============================================================================
const CONCURRENCY = 3;
const EMPTY = '–';
const DATE_TIME_FORMAT = 'HH:mm:ss dd/MM/yyyy';
const ROLES_HIDE_MONEY = ['Operating', 'Technician']; // role bị ẩn tiền đơn Nợ (nếu chưa bật userAnTien)

export type reportByOrder = {
  BranchId: number;
  BranchName: string;
  Branchphone: string;
  BranchisParent: boolean;
  BranchisSwitchBranch: boolean;
};

// Các cột cần lấy danh sách giá trị cho bộ lọc lookup
const LOOKUP_FIELDS = [
  'statusVNText',
  'orderType',
  'hinhThucThanhToan',
  'branchName',
  'staffCreate',
  'staffExecute',
  'yeuCauChinhSua',
  'isUpdatedAfterComplete',
  'isBackDoor'
] as const;

type LookupField = typeof LOOKUP_FIELDS[number];
type LookupSets = Record<LookupField, Set<any>>;

const createLookupSets = (): LookupSets =>
  Object.fromEntries(LOOKUP_FIELDS.map(f => [f, new Set()])) as unknown as LookupSets;

const createDefaultBody = (): Record<string, any> => ({
  maDonHang: '',
  orderTypeId: [0],
  from: convertTime.begin,
  to: convertTime.end,
  orderStatus: '',
  companyBranchId: 0,
  maKhachHang: '',
  loaiHinhHopTac: 0,
  staffExecute: '',
  staffCreate: '',
  companyName: '',
  hinhThucThanhToan: '',
  typeDate: 1,
  typeBranch: 0
});

const isEmptyDate = (s?: string) => !s || s.startsWith('0001-01-01');
const toDateOrDash = (s?: string) => (isEmptyDate(s) ? EMPTY : new Date(s as string));
const orDash = (v: any) => v || EMPTY;

// Cache kết quả convert theo key để không gọi lặp lại cho hàng trăm nghìn dòng
const statusCache = new Map<string, any>();
const getStatus = (cur: any, seen: any) => {
  const k = `${cur}_${seen}`;
  let v = statusCache.get(k);
  if (!v) {
    v = UtilOrder.convertStatusTextToStatusClient(cur, seen) ?? { vnText: EMPTY };
    statusCache.set(k, v);
  }
  return v;
};

const orderTypeCache = new Map<any, any>();
const getOrderType = (t: any) => {
  if (!orderTypeCache.has(t)) orderTypeCache.set(t, UtilOrder.convertOrderType(t) ?? EMPTY);
  return orderTypeCache.get(t);
};

const formatCustomSum = (value: any) => Helpers.FormatMoney(value, true);

const onRowPreparedStyle = (e: any) => {
  if (e.rowType === 'data' && e.data.isParent) e.rowElement.style.background = '#f4f4f4';
};

// ----- Format dữ liệu (mutate tại chỗ cho nhẹ với data lớn) -----
interface IFormatCtx {
  seen: Set<string>;
  seq: { n: number };
  lookup: LookupSets;
  hideMoney: boolean; // role bị giới hạn + chưa bật userAnTien -> ẩn tiền đơn Nợ
}

const formatRows = (rows: any[], ctx: IFormatCtx) => {
  for (const item of rows) {
    // Lấy giá trị GỐC trước khi ghi đè (vì mutate tại chỗ)
    const isFirst = !ctx.seen.has(item.maDonHang);
    if (isFirst) ctx.seen.add(item.maDonHang);

    const rawUpdatedAfterComplete = !!item.isUpdatedAfterComplete;
    const st = getStatus(item.currentStatus, item.operatorSeen);
    const seq = ++ctx.seq.n;

    item.index = seq;
    item.maDonHangKey = `${item.maDonHang}#${item.childId ?? seq}`; // KEY DUY NHẤT
    item.countOrder = isFirst ? 1 : 0;
    item.isParent = isFirst;
    item.status = st;
    item.statusVNText = st.vnText ?? EMPTY;
    item.sdtLienHe = orDash(item.sdtLienHe);
    item.thoiGianHoanThanh = new Date(item.thoiGianHoanThanh);
    item.thoiGianHenLap = new Date(item.thoiGianHenLap);
    item.thoiGianTao = new Date(item.thoiGianTao);
    item.orderType = getOrderType(item.orderType);
    item.lastTimeUpdateAfterComplete = rawUpdatedAfterComplete ? new Date(item.lastTimeUpdateAfterComplete) : '';
    item.companyName = item.companyName === 'AnhDuong' ? 'Ánh Dương' : 'Bình Minh';
    item.yeuCauChinhSua =
      item.yeuCauChinhSua === 2 ? 'Đã cập nhật' : item.yeuCauChinhSua === 1 ? 'Chưa cập nhật' : EMPTY;
    item.isUpdatedAfterComplete = rawUpdatedAfterComplete ? 'Có chỉnh sửa' : EMPTY;
    item.maTrang = item.maTrang !== 0 ? item.maTrang : EMPTY;
    item.tienThue = (item.tienSauThue ?? 0) - (item.soTien ?? 0);

    // Phân quyền: ẩn tiền thanh toán của đơn Nợ
    item.tienThanhToan = ctx.hideMoney && item.hinhThucThanhToan === 'Nợ' ? 0 : item.tienThanhToan ?? 0;

    item.tenSanPham = orDash(item.tenSanPham);
    item.staffCreate = orDash(item.staffCreate);
    item.staffOperate = orDash(item.staffOperate);
    item.staffExecute = orDash(item.staffExecute);
    item.staffApprove = orDash(item.staffApprove);
    item.staffCancel = orDash(item.staffCancel);
    item.staffExecuteTb = orDash(item.staffExecuteTB);
    item.userUpdateAfterComplete = orDash(item.userUpdateAfterComplete);
    item.loaiSp = orDash(item.loaiSp);
    item.hanDangKiem = toDateOrDash(item.hanDangKiem);
    item.userUpdateBackDoor = orDash(item.userUpdateBackDoor);
    item.isBackDoor = item.isBackDoor ? 'Mở lại' : EMPTY;
    item.timeUpdateBackDoor = toDateOrDash(item.timeUpdateBackDoor);

    // Thông tin thu tiền
    item.cashStatusExport =
      item.cashStatus === 'Received'
        ? 'Đã thu tiền'
        : item.cashStatus === 'Pending'
        ? 'Chưa thu'
        : item.cashStatus ?? '';
    item.debtPaidDateExport = isEmptyDate(item.debtPaidDate)
      ? ''
      : moment(item.debtPaidDate).format('DD/MM/YYYY HH:mm:ss');
    item.maDonThuNo = item.maDonThuNo || '';

    // Gom giá trị cho lookup (sau khi đã format)
    for (const f of LOOKUP_FIELDS) {
      const v = item[f];
      if (v !== undefined && v !== null) ctx.lookup[f].add(v);
    }
  }
  return rows;
};

// ----- Gọi API -----
// Fetch 1 chunk + retry lỗi mạng. Không toast ở đây, nơi gọi gom lỗi rồi báo 1 lần
const fetchChunk = async (body: any, retry = 1): Promise<{ rows: any[]; failed: boolean }> => {
  try {
    const res: any = await OrderManagerApi.GetOrderReportV5(body);
    return { rows: res?.data?.data ?? [], failed: !!res?.data?.error };
  } catch (err: any) {
    const isNetwork = !err?.response && err?.isAxiosError && err?.code !== 'ECONNABORTED';
    if (retry > 0 && isNetwork) {
      await Helpers.DelayTime(2000);
      return fetchChunk(body, retry - 1);
    }
    return { rows: [], failed: true }; // lỗi chunk này thì bỏ qua, chạy tiếp chunk khác
  }
};

// ----- Helper dựng cột -----
const dateTimeCol = (dataField: string, caption: string, extra: Partial<IColumnTable> = {}): IColumnTable =>
  ({ dataField, caption, format: DATE_TIME_FORMAT, width: 130, alignment: 'right', ...extra } as IColumnTable);

const moneyCol = (dataField: string, caption: string, width: number, extra: Partial<IColumnTable> = {}): IColumnTable =>
  ({
    dataField,
    caption,
    width,
    alignment: 'right',
    format: 'fixedPoint',
    isSumCol: true,
    dataType: 'number',
    sumCol: dataField,
    formatCustomSum,
    ...extra
  } as IColumnTable);

const lookupCol = (
  dataField: string,
  caption: string,
  width: number,
  options: any[] | undefined,
  extra: Partial<IColumnTable> = {}
): IColumnTable =>
  ({ dataField, caption, width, lookupFilter: true, sourceLookup: options ?? [], ...extra } as IColumnTable);

const renderCashStatus = (props: any) => {
  const status = props.data.cashStatus;
  const bg = status === 'Received' ? 'green' : status === 'Pending' ? 'red' : '';
  return (
    <span
      style={{
        position: 'relative',
        background: bg,
        color: 'white',
        padding: '2px 6px',
        width: 'fit-content',
        borderRadius: '2px'
      }}
    >
      {props.data.cashStatusExport}
    </span>
  );
};

// ============================================================================
// Component
// ============================================================================
export default function ReportByOrderV3() {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  // ----- Redux -----
  const timeSelected = useSelector((s: RootState) => s.select.selectedTime);
  const selectedBranch = useSelector((s: RootState) => s.select.selectedBranch);
  const orderSelected = useSelector((s: RootState) => s.select.selectedOrder);
  const statusSelected = useSelector((s: RootState) => s.select.selectedStatus);
  const orderTime = useSelector((s: RootState) => s.select.orderTime);
  const selectedArrayOrder = useSelector((s: RootState) => s.select.selectedArrayOrder);
  const loadedDataSelect = useSelector((s: RootState) => s.select.loadedData);
  const loadedData = useSelector((s: RootState) => s.createOrder.loadedData);
  const uLogin = useSelector((s: RootState) => s.user.account);
  const roleTag = useSelector((s: RootState) => s.user.roleTag);
  const userAnTien = useSelector((s: RootState) => s.user.userAnTien);

  // ----- State -----
  const [progress, setProgress] = useState<number | null>(null); // null = ẩn badge (chỉ hiện khi load nhiều ngày)
  const [lookupOptions, setLookupOptions] = useState<Record<string, any[]>>({});

  // ----- Refs -----
  const runId = useRef(0); // mỗi lần load tăng 1, dùng để huỷ lượt cũ
  const lastBodyRef = useRef<Record<string, any>>(createDefaultBody()); // filter đang xem (cho nút Reload)
  const seenRef = useRef(new Set<string>());
  const seqRef = useRef({ n: 0 });
  const lookupRef = useRef<LookupSets>(createLookupSets());

  // Các ref "luôn mới nhất" để callback ổn định (identity không đổi) vẫn đọc được giá trị hiện tại
  const hideMoneyRef = useRef(false);
  hideMoneyRef.current = !userAnTien && ROLES_HIDE_MONEY.includes(uLogin?.role);
  const filterRef = useRef<any>({});
  filterRef.current = { timeSelected, selectedBranch, orderSelected, statusSelected, orderTime, selectedArrayOrder };

  // ----- Store dữ liệu lớn -----
  const store = useMemo(() => new ArrayStore({ key: 'maDonHangKey', data: [] }), []);
  const dataSource = useMemo(
    () =>
      new DataSource({
        store,
        reshapeOnPush: true, // grid tính lại sort/summary khi có push
        pushAggregationTimeout: 500 // gom nhiều push thành 1 lần render
      }),
    [store]
  );

  const resetStore = useCallback(() => {
    seenRef.current.clear();
    seqRef.current.n = 0;
    LOOKUP_FIELDS.forEach(f => lookupRef.current[f].clear());
    setLookupOptions(prev => (Object.keys(prev).length ? {} : prev)); // đang rỗng thì giữ nguyên ref, tránh re-render thừa
    store.clear();
    dataSource.reload();
  }, [store, dataSource]);

  // trick mảng options {value,label}, gọi 1 lần khi load xong
  const flushLookup = useCallback(() => {
    setLookupOptions(
      Object.fromEntries(
        LOOKUP_FIELDS.map(f => [
          f,
          Array.from(lookupRef.current[f])
            .sort()
            .map(v => ({ value: v, label: v }))
        ])
      )
    );
  }, []);

  const pushRows = useCallback(
    (rows: any[]) => {
      const ctx: IFormatCtx = {
        seen: seenRef.current,
        seq: seqRef.current,
        lookup: lookupRef.current,
        hideMoney: hideMoneyRef.current
      };
      store.push(formatRows(rows, ctx).map(data => ({ type: 'insert', data })));
    },
    [store]
  );

  // Toast "không có dữ liệu" chỉ hiện 1 lần cho mỗi lượt query.
  // Cờ được reset khi bắt đầu query mới (runQuery) hoặc khi có dữ liệu về.
  const noDataShownRef = useRef(false);
  const showNoData = () => {
    if (noDataShownRef.current) return;
    noDataShownRef.current = true;
    message.info({ content: t('System.NoDataResponse'), key: 'no-data' });
  };

  // ===== LOAD 1 NGÀY (useOrderReportV5): cũng đi qua store =====
  const changeData = (data: any) => {
    runId.current++; // huỷ vòng lặp nhiều ngày nếu đang chạy
    resetStore();
    const rows = data?.data ?? [];
    if (rows.length) {
      noDataShownRef.current = false; // có dữ liệu -> lần rỗng sau được báo lại
      try {
        pushRows(rows);
        flushLookup(); // 1 ngày: lấy options filter sau khi push data xong
      } catch (error) {
        console.log('error', error);
      }
    } else if (!data?.error) showNoData();
    if (data?.error) message.error({ content: t('System.FetchError'), key: 'fetch-error' });
    dispatch(updatePageLoadingApi(false));
  };

  // Bỏ qua nếu cùng một kết quả bị báo onSuccess nhiều lần (chống vòng lặp changeData -> render -> onSuccess)
  const lastResultRef = useRef<any>(null);
  const handleResult = (result: any) => {
    if (lastResultRef.current === result) {
      dispatch(updatePageLoadingApi(false)); // vẫn tắt loading để không bị kẹt spinner khi bỏ qua kết quả trùng
      return;
    }
    lastResultRef.current = result;
    changeData(result);
  };

  const [, handleAdd]: any = useOrderReportV5({
    onSuccess: handleResult,
    onError: () => {
      message.error({ content: t('System.FetchError'), key: 'fetch-error' });
      dispatch(updatePageLoadingApi(false));
    }
  });

  // ===== LOAD NHIỀU NGÀY =====
  const loadByChunks = async (body: any, datesArr: string[]) => {
    const myRun = ++runId.current;
    resetStore();
    setProgress(0);
    dispatch(updatePageLoadingApi(true));

    const queue: any[] = [];
    for (let i = 0; i < datesArr.length - 1; i++) {
      queue.push({ ...body, from: datesArr[i], to: datesArr[i + 1] });
    }
    const total = queue.length;
    let done = 0;
    let failedCount = 0;
    let hasData = false;

    const worker = async () => {
      while (queue.length && myRun === runId.current) {
        const { rows, failed } = await fetchChunk(queue.shift());
        if (myRun !== runId.current) return; // lượt mới đã bắt đầu -> bỏ kết quả

        if (failed) failedCount++;
        if (rows.length) {
          try {
            pushRows(rows);
          } catch (error) {
            console.log('error', error);
          }
          if (!hasData) {
            hasData = true;
            dispatch(updatePageLoadingApi(false)); // có dữ liệu đầu tiên thì tắt loading toàn trang
          }
        }
        done++;
        setProgress(Math.round((done / total) * 100));
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));
    if (myRun !== runId.current) return;

    flushLookup(); // nhiều ngày: gọi 1 lần sau khi hoàn tất
    dispatch(updatePageLoadingApi(false));

    if (failedCount > 0) message.error({ content: t('System.FetchError'), key: 'fetch-error' }); // gom lỗi, báo 1 lần
    else if (!hasData) showNoData();

    setTimeout(() => myRun === runId.current && setProgress(null), 500);
  };

  // ===== chạy query: < 1 ngày gọi API 1 lần, >= 1 ngày tách chunk theo ngày =====
  const runQuery = async (body: Record<string, any>) => {
    lastBodyRef.current = body;
    noDataShownRef.current = false; // query mới do người dùng bấm -> cho phép báo "không có dữ liệu" lại 1 lần
    const startDate = moment(body.from);
    const endDate = moment(body.to);
    const numDay = moment.duration(endDate.diff(startDate)).asMinutes() / 1440;

    if (numDay >= 1) {
      const datesArr = Helpers.EnumerateDaysBetweenDates(
        startDate,
        endDate,
        { beginTime: body.from, endTime: body.to },
        1
      );
      await loadByChunks(body, datesArr);
    } else {
      setProgress(null);
      dispatch(updatePageLoadingApi(true));
      handleAdd(body);
    }
  };
  // luôn trỏ tới runQuery mới nhất, để các callback bên dưới giữ identity ổn định
  const runQueryRef = useRef(runQuery);
  runQueryRef.current = runQuery;

  // ----- Panel lọc -----
  // callBackEvent ổn định: SelectPanel không bị gọi lại chỉ vì component re-render
  const callBackEvent = useMemo<ICallBackEvents>(
    () => ({
      callBackOrdersReport: async (inputValue?: any, inputType?: any) => {
        const { timeSelected, selectedBranch, orderSelected, statusSelected, orderTime, selectedArrayOrder } =
          filterRef.current;
        const body = createDefaultBody();

        if (inputValue !== undefined) {
          if (inputType === 'OrderId') body.maDonHang = inputValue;
          else if (inputType === 'CustomerId') body.maKhachHang = inputValue;
        }

        if (timeSelected !== undefined) {
          body.from = timeSelected.beginTime;
          body.to = timeSelected.endTime;
        }

        body.companyBranchId = selectedBranch !== undefined ? selectedBranch.id : 0;
        if (orderSelected !== undefined) body.orderTypeId = [orderSelected.id];
        if (selectedArrayOrder && selectedArrayOrder?.length !== 0) body.orderTypeId = selectedArrayOrder?.data;
        if (orderTime !== undefined) body.typeDate = orderTime;
        if (statusSelected !== undefined) body.orderStatus = statusSelected;

        await runQueryRef.current(body);
      }
    }),
    []
  );

  // ----- Effects -----
  useEffect(() => {
    runQueryRef.current(lastBodyRef.current); // tải lần đầu đúng 1 lần
    return () => {
      runId.current++; // unmount -> huỷ các worker đang chạy
    };
  }, []);

  useEffect(() => {
    if (!loadedData.typeOrderLoaded) dispatch(getAllTypeOrders());
    if (!loadedData.productMixDataLoaded) dispatch(getInitProductMixData());
    if (!loadedDataSelect.branchLoaded) dispatch(getAllBranch());
  }, []);

  // ----- Cột -----
  const columns: IColumnTable[] = useMemo(() => {
    const hideContact = roleTag === 'WareHouse';
    const L = lookupOptions;
    const tk = (k: string) => t(`OrganizedSales.OrderManager.AllOrder.${k}`);
    const tr = (k: string) => t(`Report.ReportByOrder.${k}`);

    return [
      { dataField: 'index', caption: 'Table.STT', width: 50, editable: true, fixed: true, alignment: 'right' },
      { dataField: 'maDonHangKey', caption: tk('CodeOrder'), visible: false },
      {
        dataField: 'maDonHang',
        caption: tk('CodeOrder'),
        width: 110,
        editable: true,
        fixed: true,
        isSumCol: true,
        sumCol: 'countOrder',
        summaryType: 'sum',
        displayFormat: `${tk('Sum')}: {0} (${tk('order')})`
      },
      { dataField: 'maKhachHang', caption: tk('CustomerCode'), width: 110, fixed: true },
      { dataField: 'customerName', caption: tk('CustomerName'), width: 110, fixed: true },
      {
        dataField: 'customerPhone',
        caption: tk('CusNumberPhone'),
        width: 100,
        fixed: true,
        visible: !hideContact,
        showInColumnChooser: !hideContact
      },
      {
        dataField: 'sdtLienHe',
        caption: tk('ContactNumberPhone'),
        width: 100,
        visible: !hideContact,
        showInColumnChooser: !hideContact
      },
      {
        caption: tr('Datetime'),
        childHeader: [
          dateTimeCol('thoiGianHenLap', tk('DeliverTime')),
          dateTimeCol('thoiGianTao', tk('CreateTime')),
          dateTimeCol('thoiGianHoanThanh', tk('EndTimeOrder'))
        ]
      },
      lookupCol('statusVNText', tk('Status'), 115, L.statusVNText),
      lookupCol('orderType', tk('OrderType'), 90, L.orderType),
      {
        caption: tr('PayMoney'),
        childHeader: [
          lookupCol('hinhThucThanhToan', tk('PaymentMethod'), 120, L.hinhThucThanhToan),
          { dataField: 'mucThue', caption: tk('MucThue'), width: 80, alignment: 'right' },
          moneyCol('tienThue', tk('TienThue'), 80),
          moneyCol('tienSauThue', tk('TienSauThue'), 110),
          moneyCol('tongTienThue', tk('TongTienThue'), 110, { visible: false, showInColumnChooser: true }),
          moneyCol('tienThanhToan', tk('TotalMoney'), 80),
          moneyCol('tienThucThu', tk('PayedMoney'), 100),
          moneyCol('tienChuyenKhoanThucTe', tk('PayedBankinhMoney'), 100)
        ]
      },
      { dataField: 'tongSanPham', caption: tr('SumDetailOrder'), width: 320 },
      {
        caption: tr('DetailDevice'),
        childHeader: [
          { dataField: 'maTrang', caption: tr('CompanyId'), alignment: 'right', width: 80 },
          { dataField: 'oldMaSanPham', caption: tr('OldMaSanPham'), width: 120 },
          { dataField: 'oldSerrial', caption: tr('OldSerial'), width: 80 },
          { dataField: 'oldPhoneNumber', caption: tr('OldPhone'), width: 80 },
          { dataField: 'tenSanPham', caption: tr('MaSanPham'), width: 200 },
          { dataField: 'loaiSp', caption: tr('TypeSanPham'), width: 100 },
          { dataField: 'soLuong', caption: tr('Count'), width: 70, alignment: 'right' },
          { dataField: 'serial', caption: tr('Serial'), width: 80 },
          { dataField: 'phoneNumber', caption: tr('Phone'), width: 80 },
          { dataField: 'bs', caption: tr('Bs'), width: 80 },
          {
            dataField: 'hanDangKiem',
            caption: tk('HanDangKiem'),
            format: 'dd/MM/yyyy',
            width: 130,
            alignment: 'right'
          },
          moneyCol('soTien', tr('Cost'), 90),
          { dataField: 'staffExecuteTb', caption: tr('StaffExecute'), width: 100 },
          { dataField: 'noteThietBi', caption: tr('Note'), width: 400 }
        ]
      },
      {
        caption: tr('DetailError'),
        childHeader: [
          { dataField: 'errorType', caption: tr('ErrorTpye'), width: 120 },
          { dataField: 'phenomenon', caption: tr('Phenomenon'), width: 150 },
          { dataField: 'diagnosis', caption: tr('Diagnosis'), width: 120 },
          { dataField: 'handling', caption: tr('Handling'), width: 120 },
          { dataField: 'errorNote', caption: tr('NoteError'), width: 200 }
        ]
      },
      lookupCol('branchName', tk('BranchName'), 100, L.branchName),
      { dataField: 'companyName', caption: tr('CompanyName'), width: 100 },
      { dataField: 'diaChi', caption: tk('AddressShip'), width: 300 },
      {
        caption: tr('orderVat'),
        childHeader: [
          { dataField: 'vaTTenCongTy', caption: tk('Name'), width: 100 },
          { dataField: 'vaTMaSoThue', caption: tk('CodeVAT'), width: 100 },
          { dataField: 'vaTAddress', caption: tk('Address'), width: 250 }
        ]
      },
      {
        caption: 'Thông tin thu tiền',
        childHeader: [
          {
            dataField: 'cashStatusExport',
            caption: 'Trạng thái',
            width: 120,
            alignment: 'center',
            cellRender: renderCashStatus
          },
          { dataField: 'maDonThuNo', caption: 'Mã đơn thu nợ', width: 120 },
          { dataField: 'debtPaidDateExport', caption: 'Ngày thu tiền', width: 150 }
        ]
      },
      {
        caption: tr('Staff'),
        childHeader: [
          lookupCol('staffCreate', tk('StaffCreater'), 100, L.staffCreate),
          { dataField: 'staffOperate', caption: tk('StaffOperater'), width: 100 },
          lookupCol('staffExecute', tk('StaffProcesser'), 100, L.staffExecute),
          { dataField: 'staffApprove', caption: tk('StaffApprover'), width: 100 },
          { dataField: 'staffCancel', caption: tk('StaffCancel'), width: 100 }
        ]
      },
      {
        caption: tk('NoteInfo'),
        childHeader: [
          { dataField: 'note', caption: tk('Note'), minWidth: 300, width: 300 },
          { dataField: 'completeNote', caption: tk('NoteCompleteKT'), minWidth: 300, width: 300 }
        ]
      },
      lookupCol('yeuCauChinhSua', tk('AcceptPayment'), 120, L.yeuCauChinhSua),
      {
        caption: tk('UpdateAfterComplete'),
        childHeader: [
          lookupCol('isUpdatedAfterComplete', tk('IsUpdateAfterComplete'), 150, L.isUpdatedAfterComplete, {
            allowSorting: false
          }),
          { dataField: 'noteUpdateAfterComplete', caption: tk('NoteAfterComplete'), width: 300 },
          dateTimeCol('lastTimeUpdateAfterComplete', tk('TimeAfterComplete'), { width: 135 }),
          { dataField: 'userUpdateAfterComplete', caption: tk('UserAfterComplete'), width: 100 }
        ]
      },
      {
        caption: tk('OrderRecording'),
        childHeader: [
          lookupCol('isBackDoor', tk('IsBackDoor'), 100, L.isBackDoor, { allowSorting: false }),
          dateTimeCol('timeUpdateBackDoor', tk('TimeUpdateBackDoor'), { width: 135 }),
          { dataField: 'userUpdateBackDoor', caption: tk('UserUpdateBackDoor'), width: 150 }
        ]
      }
    ] as IColumnTable[];
  }, [t, roleTag, lookupOptions]);

  // ----- Render -----
  const title = t('OrganizedSales.ReportDetailsOrder.Title');

  const renderTable = useMemo(
    () => (
      <Table
        tableName={title}
        tableId="baocaotheodonhang"
        keyExpr="maDonHangKey"
        columns={columns}
        data={dataSource}
        oncClickReload={() => runQueryRef.current(lastBodyRef.current)} // reload đúng filter đang xem
        reload={true}
        fileNameExport={title}
        onRowPreparedStyle={onRowPreparedStyle}
        isStriped={false}
        enableLoadingPanel={true}
        hiddenAddButton={true}
        isHiddenPaging={false}
        minPageSize={MINPAGESIZE3}
        downHeight={15}
        customCenterToolbar={() => <TableTitle title={title} />}
        repaintChangesOnly={true}
      />
    ),
    [columns, dataSource, title]
  );

  return (
    <Layout path={[{ name: 'Báo cáo chi tiết đơn hàng' }]}>
      <div className="flex lg:h-fit lg:pb-2">
        <SelectPanel
          type={[EModePanelSelect.Order, EModePanelSelect.Branch, EModePanelSelect.Status, EModePanelSelect.Time]}
          isHiddenDefaultSelect={true}
          isMultipleSelect={false}
          isAllOption={true}
          checkSelect={false}
          callBackEvents={callBackEvent}
          checkType="theodon"
          isMutipleOrder={[EModePanelSelect.Order]}
          limitTimeRange={31}
        />
      </div>

      <div>
        <div className="px-1 pt-1 relative">
          {/* {progress !== null && (
            <span className="text-[13.5px] rounded italic px-1 font-semibold text-yellow-300 bg-red-600 absolute top-[-26px] right-1 z-20">
              {progress > 0 && t('OrganizedSales.ReportDetailsOrder.LoadingStatus') + ' ' + progress.toFixed(0) + '%'}
            </span>
          )} */}
          {renderTable}
        </div>
      </div>
    </Layout>
  );
}
